import { dispatchErrorToast } from 'packages/lib/common-ui/toast/api-toast';
import {
  Avatar,
  Button,
  Dropdown,
  MenuProps,
  message,
  theme as antTheme,
  Space,
  Switch,
} from 'antd';
import { signOut } from 'packages/lib/auth';
import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { logError } from 'packages/lidar-tool/utils/logger';
import {
  useAccessToken,
  useUserProfile,
} from 'packages/lib/utils/hooks/use-auth-data';
import { FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { ItemType } from 'antd/es/menu/interface';
import { useRecoilState } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';
import { BrightnessContrast } from '@carbon/icons-react';

const MENU_ICON_MARGIN_HEIGHT = 4;
const MENU_ICON_MARGIN_LEFT = 12;
const MENU_ICON_MARGIN_RIGHT = 24;
const AVATAR_BADGE_OFFSET = 9;

export const iconMarginStyle = {
  marginTop: `${MENU_ICON_MARGIN_HEIGHT}px`,
  marginBottom: `${MENU_ICON_MARGIN_HEIGHT}px`,
  marginRight: `${MENU_ICON_MARGIN_RIGHT}px`,
  marginLeft: `${MENU_ICON_MARGIN_LEFT}px`,
};

export const avatarMarginStyle = {
  marginTop: `${MENU_ICON_MARGIN_HEIGHT - AVATAR_BADGE_OFFSET}px`,
  marginBottom: `${MENU_ICON_MARGIN_HEIGHT - AVATAR_BADGE_OFFSET}px`,
  marginRight: `${MENU_ICON_MARGIN_RIGHT - AVATAR_BADGE_OFFSET}px`,
  marginLeft: `${MENU_ICON_MARGIN_LEFT - AVATAR_BADGE_OFFSET}px`,
};

/** Provides a reusable, customizable User Profile menu.
 * If you want to insert custom items into the menu, provide them in the optional extraMenuItems prop,
 * which takes the same array of objects that the Antd Dropdown takes in its 'menu.items' prop.
 * Also provide the (optional) handleOnClick handler to handle clicks in your custom item(s). This callback
 * should be of type MenuProps['onClick'] (import MenuProps from Antd)
 * passed
 * @example
 *   const extraItems: ItemType[] = [{
    key: 'my-custom-menu-item',
    label: 'Do the thing',
    icon: <MyCoolIcon />,
  }];

    <ProfileMenuButton
    extraMenuItems={extraItems}
    handleOnClick={(item) => console.log('Custom menu item selected: ', item)}
  />
 * */
export const ProfileMenuButton: FC<
  PropsWithChildren<{
    extraMenuItems?: ItemType[];
    extraMenuItemsBottom?: ItemType[];
    enableThemeToggle?: boolean;
    handleOnClick?: MenuProps['onClick'];
  }>
> = ({
  extraMenuItems = [],
  extraMenuItemsBottom = [],
  enableThemeToggle = false,
  handleOnClick,
}): JSX.Element => {
  const { userProfile } = useUserProfile();
  const { accessToken } = useAccessToken();
  const [theme, setTheme] = useRecoilState(uiThemeAtom);
  const [messageApi, contextHolder] = message.useMessage();
  const { token } = antTheme.useToken();

  const toggleTheme = (checked: boolean) => {
    setTheme(checked ? 'dark' : 'light');
  };

  const onClick: MenuProps['onClick'] = (selectedOption) => {
    const mapOptionToAction: Record<string, () => void> = {
      'user-profile-item': () => {
        return;
      },
      'sign-out-item': () => {
        signOut().catch((err) => {
          dispatchErrorToast(
            'Unable to complete your sign out request. This error has been logged. If this error persists, please contact app support.'
          );
          logError(err);
        });
      },
      'copy-access-token-item': () => {
        if (accessToken) {
          void navigator.clipboard.writeText(accessToken);
          void messageApi.open({
            type: 'success',
            content: 'Access token copied to clipboard.',
          });
        } else {
          void messageApi.open({
            type: 'success',
            content: 'Access token could not be copied. Try logging in again.',
          });
        }
      },
    };
    const standardItemHandler = mapOptionToAction[selectedOption.key];
    if (standardItemHandler) {
      standardItemHandler();
    } else {
      handleOnClick?.(selectedOption);
    }
    return;
  };

  const extraMenuItemsStyled = useMemo(() => {
    return [...extraMenuItems].map((item: ItemType) => {
      return {
        ...item,
        icon: (
          <div style={iconMarginStyle}>
            {item ? (item as { icon: ReactNode }).icon : null}{' '}
          </div>
        ),
      } as ItemType;
    });
  }, [extraMenuItems]);

  const extraMenuItemsBottomStyled = useMemo(() => {
    return extraMenuItemsBottom.map((item: ItemType) => {
      return {
        ...item,
        icon: (
          <div style={iconMarginStyle}>
            {item ? (item as { icon: ReactNode }).icon : null}{' '}
          </div>
        ),
      } as ItemType;
    });
  }, [extraMenuItemsBottom]);

  return (
    <>
      {contextHolder}
      <Dropdown
        menu={{
          items: [
            {
              key: 'user-profile-item',
              label: (
                <div>
                  <div>
                    {userProfile ? userProfile.name : 'Loading user...'}
                  </div>
                  <div>{userProfile ? userProfile.email : null}</div>
                </div>
              ),
              icon: (
                <Avatar style={avatarMarginStyle} icon={<UserOutlined />} />
              ),
            },
            {
              type: 'divider',
            },
            ...extraMenuItemsStyled,
            extraMenuItemsStyled.length > 0
              ? {
                  type: 'divider',
                }
              : null,
            ...(enableThemeToggle
              ? [
                  {
                    key: 'toggle-ui-theme',
                    label: (
                      <Space
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>Dark Mode</div>
                        <Switch
                          data-testid="profile-menu-dark-mode-toggle"
                          size="small"
                          checked={theme === 'dark'}
                          onChange={toggleTheme}
                          style={{ display: 'flex' }}
                        />
                      </Space>
                    ),
                    icon: (
                      <div style={iconMarginStyle}>
                        <BrightnessContrast
                          style={{
                            fill: token.colorText,
                            height: '16px',
                            marginLeft: -2,
                          }}
                        />
                      </div>
                    ),
                  },
                  {
                    type: 'divider' as const,
                  },
                ]
              : []),
            {
              key: 'sign-out-item',
              label: <div>Sign Out</div>,
              icon: (
                <div style={iconMarginStyle}>
                  <LogoutOutlined />
                </div>
              ),
            },
            ...extraMenuItemsBottomStyled,
          ],
          onClick,
        }}
        placement="bottomRight"
        arrow
      >
        <Button
          ghost
          type="primary"
          style={{
            marginLeft: '0.5rem',
          }}
          icon={<UserOutlined />}
        />
      </Dropdown>
    </>
  );
};
